import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import TopFilterBar, { TopFilters, ViewType } from './TopFilterBar';
import styles from './style.module.scss';
import Grid from '@mui/material/Grid';
import ListingGrid from './ListingsGrid';
import NativeSelectDropdown from './TopFilterBar/native_select_dropdown';
import { CommercialSort } from 'utils/select-options';
import { getLocalizedSortOptionsAvailable } from 'utils/localized-select-options';
import CommercialMap from './Map';
import Container from '@mui/material/Container';
import { Bounds, LatLngPoint } from 'components/search-es/types';
import { getGeohashPrecisionFromZoom, getGeohashPrecisionFromZoomMobile } from 'components/search-es/geo_helper';
import { IS_MOBILE_BREAKPOINT, useIsMobile } from 'hooks/use-size-class';
import { fetchClusters, fetchListingsByBoundary, PropertyTypeKey } from 'data/commercial/search';
import Drawer from '@mui/material/Drawer';
import MapIcon from 'components/icon/map-icon';
import Chip from '@mui/material/Chip';
import GridViewIcon from '@mui/icons-material/GridView';
import CommercialCallout from './Map/AnnotationCallout';
import MobileFilterDrawer from './mobile/FilterDrawer';
import TuneIcon from '@mui/icons-material/Tune';
import { useCommercialListingsFilter } from 'contexts/exp-commercial';
import { Namespaces } from 'types/locales';
import { SearchPageObservabilityService } from 'observability/services/search-page-observability';

import type { CommercialPropertyBoundaryItem, CommercialMapPageCluster, CommercialPropertyClusterItem } from '@zoocasa/go-search';
import { DEFAULT_MAP_ZOOM } from 'contexts/exp-commercial/listings-filter';

const SEARCH_DEBOUNCE_MS = 500;
const MOBILE_VIEW_HEIGHT = 'calc(100dvh - 230px)';
const CONTAINER_HEIGHT = 'calc(100vh - 80px)';
const LISTING_GRID_CONTAINER_SX_MAP = { maxHeight: '100%', pr: 2, py: 1, overflowY: 'auto', maxWidth: '770px' };
const LISTING_GRID_CONTAINER_SX_GRID = { maxHeight: '100%', pr: 2, py: 1, overflowY: 'auto' };

export default function ListingsSearchPageContent() {
  const { 
    filters, 
    filterOptions, 
    setFilters, 
    statesToCodesMappings,
    searchQuery, 
    setSearchQuery, 
    mapCenter,
    mapZoom,
    setMapCenter,
    setMapZoom,
    mapRegion,
    setMapRegion,
    isLoadingSearchPredictions,
    searchPredictions,
    isTyping,
  } = useCommercialListingsFilter();
  const isMobile = useIsMobile();

  const [mobileFilterDrawerOpen, setMobileFilterDrawerOpen] = useState(false);
  const [calloutDrawerOpen, setCalloutDrawerOpen] = useState(false);
  const [sort, setSort] = useState<CommercialSort>('-date');
  const [view, setView] = useState<ViewType>('map');
  const { t } = useTranslation(Namespaces.SEARCH);
  const sortOptions = getLocalizedSortOptionsAvailable(t);

  // Listings State
  const [sidePanelListings, setSidePanelListings] = useState<{ listings: CommercialPropertyBoundaryItem[]; totalCount: number; nextPage?: string }>({ listings: [], totalCount: 0, nextPage: '' });

  // Map State
  const [mapClusters, setMapClusters] = useState<CommercialMapPageCluster[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<CommercialPropertyClusterItem[]>([]);
  const [mapBounds, setMapBounds] = useState<Bounds | null>(null);
  const [isLoading, setIsLoading] = useState<{ map: boolean; grid: boolean }>({ map: true, grid: true });

  const handleFiltersChanged = useCallback((updated: Partial<TopFilters>) => {
    setFilters(updated);
  }, [setFilters]);

  const toggleView = useCallback(() => {
    setView(prev => {
      if (prev === 'grid') setMapZoom(DEFAULT_MAP_ZOOM);
      return prev === 'map' ? 'grid' : 'map';
    });
  }, [setMapZoom]);

  const handleOnMapBoundingRegionChanged = useCallback((_oldBounds: Bounds | null, newBounds: Bounds, _oldCenter: LatLngPoint | null, newCenter: LatLngPoint) => {
    setMapBounds(newBounds);
    setMapCenter(newCenter);
  }, [setMapCenter]);

  const handleOnMapZoomChanged = useCallback((_oldZoom: number, newZoom: number, currentCenter?: LatLngPoint) => {
    // Pass center to setMapZoom so it updates both zoom and center atomically
    // This ensures the center reflects the actual map position after zoom (which may shift toward cursor)
    setMapZoom(newZoom, currentCenter);
  }, [setMapZoom]);

  const getPrecision = useCallback((zoom: number) => {
    const useMobilePrecision = window.innerWidth < IS_MOBILE_BREAKPOINT;
    const adjustedZoom = useMobilePrecision ? zoom : Math.floor(zoom);
    const getPrecisionFn = useMobilePrecision ? getGeohashPrecisionFromZoomMobile : getGeohashPrecisionFromZoom;
    return getPrecisionFn(adjustedZoom);
  }, []);

  const getFilter = useCallback(() => {
    const precision = getPrecision(mapZoom);
    const { states: availableStates, cities: availableCities, propertyTypes: availablePropertyTypes } = filterOptions;

    const propertyTypes = filters.propertyTypes.length !== availablePropertyTypes.length ? [...filters.propertyTypes] as Array<PropertyTypeKey> : undefined;
    const states = filters.states.length !== availableStates.length ? [...filters.states.map(state => statesToCodesMappings[state])] : undefined;
    const cities = filters.cities.length !== availableCities.length ? [...filters.cities] : undefined;
    
    // Compute isSale based on saleLease filter
    // If both selected or none selected, isSale is undefined (show all)
    // If only "For Sale" selected, isSale is true
    // If only "For Lease" selected, isSale is false
    let isSale: boolean | undefined = undefined;
    if (filters.saleLease.length === 1) {
      isSale = filters.saleLease.includes('For Sale');
    }
    
    return {
      bounds: mapBounds,
      precision,
      propertyTypes,
      states,
      cities,
      isSale,
    };
  }, [mapBounds, mapZoom, filters, getPrecision, filterOptions, statesToCodesMappings]);

  useEffect(() => {
    if (!mapBounds || mapZoom == null) return;

    const abortController = new AbortController();
    let isAborted = false;
    const timer = setTimeout(() => {
      setIsLoading({ grid: true, map: true });
      const filter = getFilter();

      const fetchClustersPromise = fetchClusters(filter, abortController.signal);
      const fetchListingsByBoundaryPromise = fetchListingsByBoundary(filter, sort, abortController.signal);
      fetchClustersPromise.then(res => {
        if (isAborted) return;
        setMapClusters(res.clusters);
      }).catch(error => {
        if (isAborted) return;
        SearchPageObservabilityService.trackLoadFailure({
          error,
          filterData: filter as Record<string, unknown>,
        });
      });
      fetchListingsByBoundaryPromise.then(res => {
        if (isAborted) return;
        setSidePanelListings({ listings: res.properties, totalCount: res.totalCount, nextPage: res.nextPage });
      }).catch(error => {
        if (isAborted) return;
        SearchPageObservabilityService.trackLoadFailure({
          error,
          filterData: filter as Record<string, unknown>,
        });
      });
      Promise.all([fetchClustersPromise, fetchListingsByBoundaryPromise]).finally(() => {
        if (isAborted) return;
        setIsLoading({ grid: false, map: false });
      });

    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isAborted = true;
      abortController.abort();
      clearTimeout(timer);
    };
  }, [mapBounds, mapZoom, getFilter, sort, filters]);

  const handleLoadMoreListings = useCallback(() => {
    if (sidePanelListings.nextPage) {
      setIsLoading(prev => ({ ...prev, grid: true }));
      const filter = getFilter();
      fetchListingsByBoundary({ ...filter, page: sidePanelListings.nextPage }, sort).then(res => {
        setSidePanelListings(prev => (
          {
            ...prev, listings: [...prev.listings, ...res.properties],
            totalCount: res.totalCount,
            nextPage: res.nextPage,
          }));
      }).finally(() => {
        setIsLoading(prev => ({ ...prev, grid: false }));
      });
    }
  }, [sidePanelListings.nextPage, getFilter, sort]);


  const handleApplyFilters = useCallback((updatedFilters: TopFilters) => {
    setMobileFilterDrawerOpen(false);
    setFilters(updatedFilters);
    setIsLoading(prev => ({ ...prev, grid: true }));
    setSidePanelListings({ listings: [], totalCount: 0, nextPage: undefined });
  }, [setFilters]);

  const handleClusterClick = useCallback((listings: CommercialPropertyClusterItem[]) => {
    setCalloutDrawerOpen(true);
    setSelectedCluster(listings);
  }, []);

  // Memoized container styles for performance
  const containerSx = useMemo(() => {
    const baseStyles = { display: 'flex', height: CONTAINER_HEIGHT };
    return isMobile && view !== 'grid' 
      ? { ...baseStyles, m: 0, p: '0 !important' } 
      : baseStyles;
  }, [isMobile, view]);

  return (
    <Container maxWidth="xl" sx={containerSx}>
      <Grid
        container
        direction="column"
        wrap='nowrap'
      >
        {!isMobile && (
          <Grid item>
            <TopFilterBar
              options={filterOptions}
              filters={filters}
              viewType={view}
              onFiltersChanged={handleFiltersChanged}
              onToggleView={toggleView}
              onMapRegionChange={setMapRegion}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isLoadingSearchPredictions={isLoadingSearchPredictions}
              searchPredictions={searchPredictions}
              isTyping={isTyping}
            />
          </Grid>
        )}

        <Grid item flex={1} display='flex'>
          <Grid container gap={2} flexWrap='nowrap'>
            {(!isMobile || view === 'grid') && (
              <Grid flexDirection="column" container item xs={view === 'map' ? 5 : 12} alignContent='baseline'>
                <Grid container item sx={{ mb: 2 }} gap={2} alignItems='center'>
                  <span className={styles['listings-count']}>{sidePanelListings.totalCount} Listings</span>
                  <NativeSelectDropdown
                    value={sort}
                    options={sortOptions}
                    onChange={value => setSort(value as CommercialSort)}
                    ariaLabel='Sort by'
                  />
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setMobileFilterDrawerOpen(prev => !prev)}
                      className={styles.filterButton}
                    >
                      <TuneIcon /> More Filters
                    </button>
                  )}
                </Grid>
                {/* TODO: There must be a better solution like relying on flexbox */}
                <Grid item xs sx={{ maxHeight: MOBILE_VIEW_HEIGHT }}>
                  <ListingGrid
                    listings={sidePanelListings.listings}
                    viewType={view}
                    isLoadingMore={isLoading.grid}
                    onLoadMore={handleLoadMoreListings}
                    hasMore={sidePanelListings.nextPage !== undefined}
                    containerSx={view === 'map' ? LISTING_GRID_CONTAINER_SX_MAP : LISTING_GRID_CONTAINER_SX_GRID}
                  />
                </Grid>
              </Grid>
            )}
            {view === 'map' && (
              <Grid container item xs={!isMobile ? 7 : true}>
                <CommercialMap
                  clusters={mapClusters}
                  center={mapCenter}
                  region={mapRegion}
                  zoom={mapZoom}
                  disableCallout={isMobile}
                  showsLoading={isLoading.map}
                  onMapBoundingRegionChanged={handleOnMapBoundingRegionChanged}
                  onMapZoomChanged={handleOnMapZoomChanged}
                  onFilterIconClick={() => setMobileFilterDrawerOpen(prev => !prev)}
                  onClusterClick={handleClusterClick}
                />
              </Grid>
            )}
            {isMobile && (
              <Chip
                label={view === 'map' ? 'Grid' : 'Map'}
                icon={view === 'map' ? <GridViewIcon /> : <MapIcon />}
                className={styles['view-type-chip-button']}
                variant="outlined"
                size="medium"
                onClick={toggleView}
                sx={{ position: 'absolute', right: '50%', bottom: 10 }} />
            )}
            {isMobile && view !== 'grid' && (
              <Drawer
                anchor="bottom"
                open={calloutDrawerOpen}
                onClose={() => { setCalloutDrawerOpen(false); }}
                PaperProps={{ className: styles.bottomCalloutDrawerPaper }}
              >
                <CommercialCallout
                  count={selectedCluster?.length || 0}
                  listings={selectedCluster || []}
                  onClose={() => setCalloutDrawerOpen(false)}
                  containerSx={isMobile ? { borderRadius: '20px 20px 0 0' } : undefined}
                />
              </Drawer>
            )}

            <MobileFilterDrawer
              open={mobileFilterDrawerOpen}
              setOpen={setMobileFilterDrawerOpen}
              filters={filters}
              options={filterOptions}
              onApplyFilters={handleApplyFilters}
            />
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}